import { Link } from "react-router-dom";
import type { RunView } from "./api";
import { fileHref, UI, useLang } from "./lang";
import { formatTime } from "../lib/when";

const SHORT_DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
/** "2026-09-26" → "26 Sep". */
const shortDay = (d: string) => SHORT_DAY.format(new Date(`${d}T00:00:00Z`)).replace("Sept", "Sep");

export const ROLE: Record<string, { en: string; ar: string }> = {
  cxo: { en: "the Supervisor", ar: "المشرف" },
  operator: { en: "a Team member", ar: "عضو الفريق" },
  reviewer: { en: "the Compliance reviewer", ar: "مراجع الامتثال" },
  agent_handler: { en: "the Agent handler", ar: "مسؤول الوكلاء" },
  approver: { en: "the Approver", ar: "المعتمد" },
};
export const DECISION: Record<string, { en: string; ar: string }> = {
  approved: { en: "Approved", ar: "تمت الموافقة" },
  rejected: { en: "Rejected", ar: "رُفض" },
  needs_more_info: { en: "Sent back for more", ar: "أُعيد لمزيد من المعلومات" },
};

/**
 * What came back (NEW-CS-4): the headline in words, the pack's tiles from
 * calculated fields, one tap to how it was made (NEW-UX-7), and the pack.
 */
export function ResultCard({
  run,
  line,
  workspaceId,
  askedAt,
}: {
  run: RunView;
  line: { en: string; ar: string };
  workspaceId: string;
  /** In Reports: when it was asked for, so a list of cards can be told apart. */
  askedAt?: string;
}) {
  const { t, lang } = useLang();
  const headline = lang === "ar" && run.headline_ar ? run.headline_ar : run.headline_en;
  return (
    <div className="pwa-card" data-testid={`result-${run.run_id}`}>
      <div className="ttl">{t({ en: run.hand_name, ar: run.hand_name_ar ?? run.hand_name })}</div>
      {askedAt ? (
        <div className="sub">
          {t({ en: `Asked ${formatTime(askedAt)}`, ar: `طُلب ${formatTime(askedAt)}` })}
          {run.as_of ? t({ en: ` · for ${shortDay(run.as_of)}`, ar: ` · عن ${shortDay(run.as_of)}` }) : null}
        </div>
      ) : null}
      {headline ? <div style={{ margin: "4px 0" }}>{headline}</div> : null}
      {(run.checks_failed ?? []).length > 0 ? (
        <div className="sub" data-testid={`held-${run.run_id}`} style={{ margin: "4px 0" }}>
          {t(UI.draftHeld)}
        </div>
      ) : null}
      {run.tiles.length > 0 ? (
        <div className="pwa-tiles">
          {run.tiles.map((tile) => (
            <div className="pwa-tile" key={tile.label_en}>
              <div className="lbl">{lang === "ar" ? tile.label_ar : tile.label_en}</div>
              <div className="val">{tile.value}</div>
            </div>
          ))}
        </div>
      ) : null}
      {run.marking === "confidential" || run.marking === "regulated_pii" ? (
        <div className="sub" data-testid={`marked-${run.run_id}`} style={{ margin: "4px 0" }}>
          {t(UI.marked)}
        </div>
      ) : null}
      {run.fork?.resolution ? (
        <div className="sub" data-testid={`decided-${run.run_id}`}>
          {(() => {
            const r = run.fork.resolution;
            const d = DECISION[r.decision] ?? { en: r.decision, ar: r.decision };
            const who = ROLE[r.resolved_role] ?? { en: r.resolved_role, ar: r.resolved_role };
            return (
              <>
                {t({ en: `${d.en} by ${who.en}: `, ar: `${d.ar} من ${who.ar}: ` })}
                <bdi>{lang === "ar" ? `«${r.reason}»` : `“${r.reason}”`}</bdi>
              </>
            );
          })()}
        </div>
      ) : (
        <div className="sub">{t(line)}</div>
      )}
      <div className="pwa-links">
        <Link to={`/app/${workspaceId}/runs/${run.run_id}`}>{t(UI.howMade)}</Link>
        {run.pack_url ? <a href={run.pack_url}>{t(UI.downloadPack)}</a> : null}
        {/* The run's other files; the pack's own slides are the link above. */}
        {(run.files ?? []).filter((f) => f.url !== run.pack_url).map((f) => (
          <a key={f.url} href={fileHref(f.url, lang)}>{t(UI[`file_${f.format}`])}</a>
        ))}
      </div>
    </div>
  );
}
